import type { DishAnalysis } from '@/common/generated/wine-dish-analysis.typebox';
import {
  AppstoreOutlined,
  CoffeeOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';
import { Card, Progress } from 'antd';

interface KeyCharacteristicsCardProps {
  analysis: DishAnalysis;
}

export function KeyCharacteristicsCard({
  analysis,
}: KeyCharacteristicsCardProps) {
  return (
    <Card
      title={
        <span>
          <AppstoreOutlined className="mr-2" />
          Key Characteristics
        </span>
      }
      size="small"
    >
      <div className="space-y-4">
        <div>
          <div className="text-sm text-gray-500 mb-1">Intensity</div>
          <Progress
            percent={
              analysis.overallIntensity === 'light'
                ? 33
                : analysis.overallIntensity === 'medium'
                  ? 66
                  : 100
            }
            showInfo={false}
          />
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Temperature</div>
          <div>{analysis.textureProfile.temperature}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Regional Suggestions</div>
          <div className="flex flex-wrap gap-2">
            {analysis.pairingConsiderations.regionalSuggestions?.map(
              (region) => (
                <div key={region} className="bg-gray-50 px-2 py-1 rounded-md">
                  {region}
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

interface IngredientsMethodsCardProps {
  analysis: DishAnalysis;
}

export function IngredientsMethodsCard({
  analysis,
}: IngredientsMethodsCardProps) {
  return (
    <Card
      title={
        <span>
          <CoffeeOutlined className="mr-2" />
          Ingredients & Methods
        </span>
      }
      size="small"
    >
      <div className="space-y-4">
        <div>
          <div className="text-sm text-gray-500 mb-1">Main Ingredients</div>
          <div className="space-y-1">
            {analysis.ingredients
              .filter((i) => i.role === 'primary')
              .map((ingredient) => (
                <div key={ingredient.name}>{ingredient.name}</div>
              ))}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Cooking Methods</div>
          <div className="space-y-1">
            {analysis.cookingMethods.map((method) => (
              <div key={method.technique}>
                {method.technique}
                {method.temperature && ` (${method.temperature})`}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}

interface FlavorProfileCardProps {
  analysis: DishAnalysis;
}

export function FlavorProfileCard({ analysis }: FlavorProfileCardProps) {
  return (
    <Card
      title={
        <span>
          <ExperimentOutlined className="mr-2" />
          Flavor Profile
        </span>
      }
      size="small"
    >
      <div className="space-y-4">
        <div>
          <div className="text-sm text-gray-500 mb-1">Primary Flavors</div>
          <div className="flex flex-wrap gap-2">
            {analysis.flavorProfile.dominant.map((flavor) => (
              <div key={flavor} className="bg-gray-50 px-2 py-1 rounded-md">
                {flavor}
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">
            Complementary Flavors
          </div>
          <div className="flex flex-wrap gap-2">
            {analysis.pairingConsiderations.complementaryFlavors.map(
              (flavor) => (
                <div key={flavor} className="bg-gray-50 px-2 py-1 rounded-md">
                  {flavor}
                </div>
              ),
            )}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Texture</div>
          <div className="flex flex-wrap gap-2">
            {analysis.textureProfile.primary.map((texture) => (
              <div key={texture} className="bg-gray-50 px-2 py-1 rounded-md">
                {texture}
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500 mb-1">Secondary Flavors</div>
          <div className="flex flex-wrap gap-2">
            {analysis.flavorProfile.secondary.map((flavor) => (
              <div key={flavor} className="bg-gray-50 px-2 py-1 rounded-md">
                {flavor}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
